import {buttonColors} from "../../../../Pickers/InputTheme";
import {Button} from "../../../../Form/Button/Button";
import {useContext, useState, useMemo} from "react";
import {GridReactContext} from "../../../../../Utils/DataGridWrapper/GridContext";
import {SADeleteUsersPermanentlyConfirmationModal} from "../SADeleteUsersPermanentlyConfirmationModal/SADeleteUsersPermanentlyConfirmationModal";
import {I18nReactContext} from "../../../../../contexts/I18nContext";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

/**
 * Super Admin specific button for permanently deleting users in bulk.
 * Opens a confirmation modal with typed confirmation before deletion.
 * CRITICAL: This action cannot be undone.
 * Only enabled when all selected users are already soft-deleted (have deletedAt).
 *
 * @param {object} params - The component parameters
 * @param {function} params.onSuccess - Callback function called after successful deletion
 * @returns {JSX.Element} The delete users permanently button component
 */
export const SADeleteUsersPermanentlyTableButton = ({onSuccess}) => {
    const {i18n} = useContext(I18nReactContext);
    const [deleteUsersPermanentlyModalOpen, setDeleteUsersPermanentlyModalOpen] = useState(false)
    const gridContext = useContext(GridReactContext)

    // Check if all selected users are already soft-deleted
    const allSelectedUsersAreDeleted = useMemo(() => {
        const selectedRows = gridContext.selectedRows || [];
        if (selectedRows.length === 0) {
            return false;
        }
        // All selected users must have deletedAt field set (soft-deleted)
        return selectedRows.every(user => user.deletedAt != null);
    }, [gridContext.selectedRows]);

    const onConfirmationModalClose = () => {
        setDeleteUsersPermanentlyModalOpen(false)
    }

    const onClick = () => {
        setDeleteUsersPermanentlyModalOpen(true)
    }

    const innerOnSuccess = () => {
        setDeleteUsersPermanentlyModalOpen(false)
        gridContext.setSelectionModelToContext([])
        gridContext.triggerTableRefresh()
        onSuccess && onSuccess()
    }

    // Button is disabled if:
    // 1. No users selected, OR
    // 2. Not all selected users are soft-deleted (have deletedAt)
    const isButtonDisabled = !gridContext.selectionModel ||
                            gridContext.selectionModel.length === 0 ||
                            !allSelectedUsersAreDeleted;

    return <div>
        <Button
            data-testid="sa-delete-users-permanently-button"
            disabled={isButtonDisabled}
            color={buttonColors.rejection}
            onClick={onClick}
            icon={<DeleteForeverIcon/>}
            text={i18n.translate('SARemoveUser.permanentlyDeleteUser')}
        />
        {deleteUsersPermanentlyModalOpen && (
            <SADeleteUsersPermanentlyConfirmationModal
                deleteUsersPermanentlyModalOpen={deleteUsersPermanentlyModalOpen}
                onCloseDeleteUsersPermanentlyModal={onConfirmationModalClose}
                onSuccess={innerOnSuccess}
            />
        )}
    </div>
}
